/*
 * Life for the still study art, drawn at its own 640 × 360 pixel size: candle and lantern flames lean
 * left and right, the cauldron bubbles, the fire beneath it flickers and throws sparks. Each effect works
 * from the art's own pixels, so it stays in the picture's palette.
 */

type RGBA = Uint8ClampedArray;
const W = 640, H = 360;

/* Flames in the art (bounding boxes of their bright pixels, art pixels) */
const FLAMES = [
  [259, 17, 262, 22], [272, 12, 276, 18], [286, 12, 291, 18], [302, 8, 305, 15],      // the candle wheel
  [318, 8, 323, 15], [332, 12, 337, 18], [348, 12, 352, 18], [361, 15, 365, 22],
  [180, 114, 184, 121],                                                              // wall lamp
  [382, 163, 389, 172], [443, 148, 455, 158],                                        // board and window candles
  [597, 220, 608, 228],                                                              // tall candle, right
  [66, 264, 69, 271],                                                                // the candle under glass, left
];
const CAULDRON = { x0: 490, x1: 566, rim: 213 };
const FIRE = { x0: 490, x1: 572, y0: 244, y1: 282 };
const STEAM = { x: 527, y: 206 };

const bright = (d: RGBA, i: number) => d[i] > 225 && d[i + 1] > 180;

export class StudyLife {
  private base: RGBA;
  private out: ImageData;
  private firePx: number[] = [];
  constructor(composite: RGBA) {
    this.base = composite;
    this.out = new ImageData(W, H);
    for (let y = FIRE.y0; y <= FIRE.y1; y++) for (let x = FIRE.x0; x <= FIRE.x1; x++) {
      const i = (y * W + x) * 4, r = composite[i], g = composite[i + 1];
      // Only the flames' own colours, and never the desk's front edge below-left of them
      const flame = (r >= 160 && g <= 90) || (r > 200 && g > 140) || (r > 178 && g < 115);
      if (flame && !(y > 274 && x < 506)) this.firePx.push(y * W + x);
    }
  }

  private put(x: number, y: number, r: number, g: number, b: number) {
    if (x < 0 || y < 0 || x >= W || y >= H) return;
    const o = (y * W + x) * 4, d = this.out.data;
    d[o] = r; d[o + 1] = g; d[o + 2] = b; d[o + 3] = 255;
  }
  private copy(x: number, y: number, fromX: number, fromY: number) {
    const s = (fromY * W + fromX) * 4, b = this.base;
    this.put(x, y, b[s], b[s + 1], b[s + 2]);
  }

  /** Draw one frame into the overlay; everything outside the effects stays transparent */
  draw(ctx: CanvasRenderingContext2D, t: number) {
    this.out.data.fill(0);
    const b = this.base;

    // Flames lean with a slow sway, the tip moving most and the wick not at all
    FLAMES.forEach(([x0, y0, x1, y1], n) => {
      const sway = Math.round(Math.sin(t * (2.3 + n * 0.37) + n * 1.7) * 1.6);
      const h = y1 - y0 + 1;
      for (let y = y0; y <= y1; y++) {
        const shift = Math.round(sway * (1 - (y - y0) / h));
        // Fill the row with the glow just beside the flame, then lay the flame pixels back shifted
        const fill = (y * W + Math.min(W - 1, x1 + 2)) * 4;
        for (let x = x0 - 2; x <= x1 + 2; x++) {
          const i = (y * W + x) * 4;
          if (bright(b, i)) this.put(x, y, b[fill], b[fill + 1], b[fill + 2]);
        }
        for (let x = x0; x <= x1; x++) {
          const i = (y * W + x) * 4;
          if (bright(b, i)) this.copy(x + shift, y, x, y);
        }
      }
      // Now and then the tip flares a pixel higher
      if (Math.sin(t * 7.1 + n * 2.3) > 0.7) { const i = (y0 * W + Math.round((x0 + x1) / 2)) * 4; this.put(Math.round((x0 + x1) / 2) + sway, y0 - 1, b[i], b[i + 1], b[i + 2]); }
    });

    // Fire under the cauldron: embers brighten and dim pixel by pixel, sparks drift up
    // Flicker in small patches (4 × 3 art pixels) so it reads as tongues of flame rather than noise
    const seed = Math.floor(t * 6);
    for (const p of this.firePx) {
      const x = p % W, y = Math.floor(p / W), i = p * 4;
      const cell = Math.floor(x / 5) * 131 + Math.floor((y + seed) / 4) * 17;
      const h = Math.sin(cell * 12.9898 + seed * 78.233) * 43758.5453, v = h - Math.floor(h);
      if (v < 0.1 && b[i] > 200) this.put(x, y, 250, 210, 90);
      else if (v < 0.2) this.put(x, y, 226, 110, 18);
      else if (v > 0.88) this.put(x, y, b[i] * 0.72, b[i + 1] * 0.6, b[i + 2] * 0.6);
    }
    for (let s = 0; s < 9; s++) {
      const life = (t * 0.9 + s * 0.37) % 1;
      const sx = FIRE.x0 + 6 + ((s * 9 + Math.floor((t * 0.9 + s * 0.37)) * 13) % (FIRE.x1 - FIRE.x0 - 12));
      const sy = Math.round(FIRE.y0 + 4 - life * 26);
      if (life < 0.85) this.put(sx + Math.round(Math.sin(life * 6 + s) * 2), sy, life < 0.4 ? 255 : 230, life < 0.4 ? 210 : 130, 40);
    }

    // Steam: soft puffs leave the brew, swell, sway and thin out as they climb
    for (let s = 0; s < 22; s++) {
      const life = (t * 0.1 + s / 22) % 1;
      const cx = STEAM.x + Math.sin(life * 5 + s * 1.3) * (3 + life * 10) + life * 14;
      const cy = STEAM.y - 2 - life * 120;
      const rad = 3 + life * 10;
      const keep = Math.min(1, 1.3 - life * 1.1);
      for (let dy = -rad; dy <= rad; dy++) for (let dx = -rad; dx <= rad; dx++) {
        if (dx * dx + dy * dy > rad * rad) continue;
        const px = Math.round(cx + dx), py = Math.round(cy + dy);
        const h = Math.sin(px * 12.9898 + py * 78.233 + s * 3.1) * 43758.5453, v = h - Math.floor(h);
        if (v > keep * (1 - (dx * dx + dy * dy) / (rad * rad + 1) * 0.6)) continue;
        // Read the art behind and mix toward steam, so it's a veil rather than paint
        const o = (py * W + px) * 4, bk = this.base, m = 0.45 + (1 - life) * 0.35 - (dy > 0 ? 0.1 : 0);
        if (px < 0 || py < 0 || px >= W || py >= H) continue;
        this.put(px, py, bk[o] + (232 - bk[o]) * m, bk[o + 1] + (224 - bk[o + 1]) * m, bk[o + 2] + (184 - bk[o + 2]) * m);
      }
    }

    // Cauldron: bubbles rise from the brew, swell and pop
    for (let s = 0; s < 9; s++) {
      const life = (t * (0.5 + s * 0.07) + s * 0.29) % 1;
      const bx = CAULDRON.x0 + 3 + ((s * 7 + Math.floor(t * (0.5 + s * 0.07) + s * 0.29) * 5) % (CAULDRON.x1 - CAULDRON.x0 - 6));
      const by = Math.round(CAULDRON.rim - life * 14);
      if (life < 0.75) {
        this.put(bx, by, 170, 214, 120);
        if (life > 0.35) { this.put(bx + 1, by, 120, 170, 80); this.put(bx, by - 1, 200, 236, 150); this.put(bx + 1, by - 1, 150, 196, 104); }
      } else if (life < 0.85) { // pop
        this.put(bx - 1, by, 200, 236, 150); this.put(bx + 2, by, 200, 236, 150); this.put(bx, by - 2, 200, 236, 150);
      }
    }
    ctx.putImageData(this.out, 0, 0);
  }
}

/** Load the study art into a pixel buffer */
export async function loadComposite(...srcs: string[]): Promise<RGBA> {
  const load = (src: string) => new Promise<HTMLImageElement>((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = src; });
  const imgs = await Promise.all(srcs.map(load));
  const c = document.createElement('canvas'); c.width = W; c.height = H;
  const x = c.getContext('2d')!; x.imageSmoothingEnabled = false; imgs.forEach(i => x.drawImage(i, 0, 0));
  return x.getImageData(0, 0, W, H).data;
}
